import {
	clearActiveScene,
	setDesignSystemLoadError,
	setDsHasDraft,
	setDsPreparing,
	setLoadingState,
	setSceneLoadError,
	updateActiveDesignSystemVersion,
	updateActiveDsChat,
	updateActiveFrameChat,
	updateActiveSceneChat,
	updateDsChatRefs,
	updateDsVersionRefs,
	updateGuestFrames,
	updateGuestScenes,
	updateScenes,
	updateUser,
} from 'redux/db/actions';
import { setDesignSystemContent } from 'redux/mode/actions';
import store from 'redux/store';
import { validate as validateUuid } from 'uuid';
import { DesignSystemVersionPollingStream } from './DesignSystemVersionPollingStream';
import { DsChatPollingStream } from './DsChatPollingStream';
import { FrameChatPollingStream } from './FrameChatPollingStream';
import { FramePollingStream } from './FramePollingStream';
import { ProfilePollingStream } from './ProfilePollingStream';
import { SceneChatPollingStream } from './SceneChatPollingStream';
import { ScenePollingStream } from './ScenePollingStream';
import { SessionPollingStream } from './SessionPollingStream';

class StreamSync {
	// Data to track - currently matches the Redux store
	private _userReady: boolean = false;
	private _activeSceneId: string | null = null;
	private _activeSceneChatId: string | null = null;
	private _activeDesignSystemId: string | null = null;
	private _activeFrameChatId: string | null = null;
	private _activeDsChatId: string | null = null;
	private _historicalDsVersionNumber: number | null = null;

	// A brand-new chat is a client-only draft with no server row until its first message
	// is sent. Its id is a valid uuid, so track it to skip the chat poll while it's a
	// draft and start polling once it becomes a real chat.
	private _draftSceneChatId: string | null = null;
	private _draftDsChatId: string | null = null;

	// ** 1 **
	// streams are based on authUserId
	private _profileStream: ProfilePollingStream | null = null;
	private _sessionStream: SessionPollingStream | null = null;

	// ** 2 **
	// streams based on activeSceneId
	private _activeSceneFramesStream: FramePollingStream | null = null;
	private _activeSceneStream: ScenePollingStream | null = null;

	// ** 2b **
	// stream based on activeSceneChatId
	private _activeSceneChatStream: SceneChatPollingStream | null = null;

	// ** 4 **
	// stream based on activeDesignSystemId
	private _activeDesignSystemVersionStream: DesignSystemVersionPollingStream | null = null;

	// ** 2c **
	// stream based on activeFrameChatId
	private _activeFrameChatStream: FrameChatPollingStream | null = null;

	// ** 5 **
	// stream based on activeDsChatId
	private _activeDsChatStream: DsChatPollingStream | null = null;

	constructor() {
		// These are values in the Redux store that we need to track.
		// For example when the activeSceneId changes from null to a scene ID, we
		// will start streaming the frames and chats and storing them in Redux.
		// Conversely, when the activeSceneId changes to null, we
		// will stop streaming the frames and chats and remove them from Redux.
		store.subscribe(() => {
			const state = store.getState();
			this.userReady = state.mode.userReady;
			this.activeSceneId = state.mode.activeSceneId;
			// Each active chat and its draft are applied together, from the same
			// snapshot: which chat to poll depends on both, and they can move in a
			// single update.
			this.applySceneChatIds(
				state.mode.activeSceneChatId,
				state.mode.draftSceneChat?.id ?? null
			);
			this.activeFrameChatId = state.mode.activeFrameChatId;
			this.activeDesignSystemId = state.mode.activeDesignSystemId;
			this.applyDsChatIds(state.mode.activeDsChatId, state.mode.draftDsChat?.id ?? null);
			this.historicalDsVersionNumber = state.mode.historicalDsVersionNumber;
		});
	}

	// ** 1 **
	set userReady(ready: boolean) {
		if (ready === this._userReady) return;
		this._userReady = ready;

		if (ready) {
			const authUser = store.getState().mode.authUser;
			this.onAuthUserIdChanged(authUser?.id ?? null);
		} else {
			this.onAuthUserIdChanged(null);
		}
	}

	// ** 2 **
	set activeSceneId(id: string | null) {
		if (id !== this._activeSceneId) {
			this._activeSceneId = id;
			this.onActiveSceneIdChanged(id);
		}
	}

	// ** 2b **
	// A chat's stream depends on the active id and on whether that chat is still an
	// unsent draft, so both are applied at once. Two cases start a stream:
	// the active chat moved, or the active chat stopped being a draft — the latter
	// being a draft that has just been sent and now has a server row. A draft that
	// is abandoned instead moves the active id in the same update, which is what
	// tells the two apart.
	private applySceneChatIds(activeId: string | null, draftId: string | null) {
		const activeChanged = activeId !== this._activeSceneChatId;
		const materialized =
			this._draftSceneChatId !== null &&
			draftId === null &&
			this._draftSceneChatId === this._activeSceneChatId &&
			!activeChanged;

		this._activeSceneChatId = activeId;
		this._draftSceneChatId = draftId;

		if (activeChanged || materialized) {
			this.onActiveSceneChatIdChanged(activeId);
		}
	}

	// ** 5 **
	private applyDsChatIds(activeId: string | null, draftId: string | null) {
		const activeChanged = activeId !== this._activeDsChatId;
		const materialized =
			this._draftDsChatId !== null &&
			draftId === null &&
			this._draftDsChatId === this._activeDsChatId &&
			!activeChanged;

		this._activeDsChatId = activeId;
		this._draftDsChatId = draftId;

		if (activeChanged || materialized) {
			this.onActiveDsChatIdChanged(activeId);
		}
	}

	// ** 2c **
	set activeFrameChatId(id: string | null) {
		if (id !== this._activeFrameChatId) {
			this._activeFrameChatId = id;
			this.onActiveFrameChatIdChanged(id);
		}
	}

	// ** 4 **
	set activeDesignSystemId(id: string | null) {
		if (id !== this._activeDesignSystemId) {
			this._activeDesignSystemId = id;
			this.onActiveDesignSystemIdChanged(id);
		}
	}

	// ** 4b **
	set historicalDsVersionNumber(versionNumber: number | null) {
		if (versionNumber !== this._historicalDsVersionNumber) {
			this._historicalDsVersionNumber = versionNumber;
			this.onHistoricalDsVersionNumberChanged(versionNumber);
		}
	}

	// ** 1 **
	onAuthUserIdChanged(userId: string | null) {
		this._profileStream?.stop('onAuthUserIdChanged');
		this._sessionStream?.stop('onAuthUserIdChanged');

		if (!userId) {
			// Stop all streams since user is logging out
			this._activeSceneFramesStream?.stop('onAuthUserIdChanged');
			this._activeSceneStream?.stop('onAuthUserIdChanged');
			this._activeSceneChatStream?.stop('onAuthUserIdChanged');
			this._activeFrameChatStream?.stop('onAuthUserIdChanged');
			this._activeDesignSystemVersionStream?.stop('onAuthUserIdChanged');
			this._activeDsChatStream?.stop('onAuthUserIdChanged');

			// clear old scenes and frames -- user Id is null so we should not have access to them
			store.dispatch(updateScenes([]));
			store.dispatch(updateGuestScenes([]));
			store.dispatch(updateGuestFrames([]));
			store.dispatch(clearActiveScene());
			return;
		}

		// Start session stream for guest scenes and frames
		// (this list should only change when page is refreshed, which should also cause userId to change and this to be called again)
		this._sessionStream = new SessionPollingStream();
		this._sessionStream.start();

		if (userId === 'guest') {
			store.dispatch(setLoadingState({ resource: 'profile', state: 'loaded' }));
			store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));
			store.dispatch(
				updateUser({
					id: 'guest',
					displayName: 'Guest',
					metadata: {},
					writeId: '',
					createdAt: new Date(),
					updatedAt: new Date(),
				})
			);
			return;
		}

		store.dispatch(setLoadingState({ resource: 'profile', state: 'loading' }));
		store.dispatch(setLoadingState({ resource: 'scenes', state: 'loading' }));

		this._profileStream = new ProfilePollingStream();
		this._profileStream.start();
	}

	// ** 2 **
	onActiveSceneIdChanged(activeSceneId: string | null) {
		this._activeSceneFramesStream?.stop('onActiveSceneIdChanged');
		this._activeSceneStream?.stop('onActiveSceneIdChanged');
		store.dispatch(setSceneLoadError(null));
		// Scene-scoped db state (frames, chat refs, comments) is only valid for the
		// scene it was polled from — drop it before the new streams start so the
		// previous scene's data is never observable under the new id.
		store.dispatch(clearActiveScene());

		if (activeSceneId) {
			if (!validateUuid(activeSceneId)) {
				store.dispatch(setSceneLoadError('Invalid scene ID'));
				return;
			}

			this._activeSceneFramesStream = new FramePollingStream(activeSceneId);
			this._activeSceneStream = new ScenePollingStream(activeSceneId);
			this._activeSceneFramesStream.start();
			this._activeSceneStream.start();
		}
	}

	// ** 2b **
	onActiveSceneChatIdChanged(activeSceneChatId: string | null) {
		this._activeSceneChatStream?.stop('onActiveSceneChatIdChanged');
		store.dispatch(updateActiveSceneChat(null));
		store.dispatch(setLoadingState({ resource: 'activeScene.chats', state: 'never-loaded' }));

		if (activeSceneChatId) {
			if (!validateUuid(activeSceneChatId)) {
				console.error('Invalid scene chat ID:', activeSceneChatId);
				return;
			}
			// A draft has no server row yet — don't poll it. Polling begins once it
			// becomes a real chat and the draft id clears.
			if (activeSceneChatId === this._draftSceneChatId) {
				this._activeSceneChatStream = null;
				return;
			}
			this._activeSceneChatStream = new SceneChatPollingStream(activeSceneChatId);
			this._activeSceneChatStream.start();
		} else {
			this._activeSceneChatStream = null;
		}
	}

	// ** 2c **
	onActiveFrameChatIdChanged(activeFrameChatId: string | null) {
		this._activeFrameChatStream?.stop('onActiveFrameChatIdChanged');
		store.dispatch(updateActiveFrameChat(null));
		store.dispatch(setLoadingState({ resource: 'activeScene.frameChat', state: 'never-loaded' }));

		if (activeFrameChatId) {
			if (!validateUuid(activeFrameChatId)) {
				console.error('Invalid frame chat ID:', activeFrameChatId);
				return;
			}
			this._activeFrameChatStream = new FrameChatPollingStream(activeFrameChatId);
			this._activeFrameChatStream.start();
		} else {
			this._activeFrameChatStream = null;
		}
	}

	// ** 4 **
	onActiveDesignSystemIdChanged(activeDesignSystemId: string | null) {
		this._activeDesignSystemVersionStream?.stop('onActiveDesignSystemIdChanged');
		store.dispatch(setDesignSystemLoadError(null));
		store.dispatch(updateActiveDesignSystemVersion(null));
		store.dispatch(setDesignSystemContent(null));
		store.dispatch(updateDsChatRefs([]));
		store.dispatch(setLoadingState({ resource: 'dsChatRefs', state: 'never-loaded' }));
		store.dispatch(updateDsVersionRefs([]));
		store.dispatch(setDsPreparing(false));
		store.dispatch(setDsHasDraft(false));
		store.dispatch(
			setLoadingState({ resource: 'activeDesignSystemVersion', state: 'never-loaded' })
		);

		if (activeDesignSystemId) {
			if (!validateUuid(activeDesignSystemId)) {
				store.dispatch(setDesignSystemLoadError('Invalid design system ID'));
				return;
			}
			this._activeDesignSystemVersionStream = new DesignSystemVersionPollingStream(
				activeDesignSystemId
			);
			this._activeDesignSystemVersionStream.start();
		}
	}

	// ** 4b **
	onHistoricalDsVersionNumberChanged(versionNumber: number | null) {
		if (!this._activeDesignSystemId) return;

		this._activeDesignSystemVersionStream?.stop('onHistoricalDsVersionNumberChanged');
		store.dispatch(setDesignSystemContent(null));
		store.dispatch(setLoadingState({ resource: 'activeDesignSystemVersion', state: 'loading' }));

		this._activeDesignSystemVersionStream = new DesignSystemVersionPollingStream(
			this._activeDesignSystemId,
			versionNumber ?? undefined
		);
		this._activeDesignSystemVersionStream.start(true);
	}

	// ** 5 **
	onActiveDsChatIdChanged(activeDsChatId: string | null) {
		this._activeDsChatStream?.stop('onActiveDsChatIdChanged');
		store.dispatch(updateActiveDsChat(null));
		store.dispatch(setLoadingState({ resource: 'activeDsChat', state: 'never-loaded' }));

		if (activeDsChatId) {
			if (!validateUuid(activeDsChatId)) {
				console.error('Invalid DS chat ID:', activeDsChatId);
				return;
			}
			// A draft has no server row yet — don't poll it. Polling begins once it
			// becomes a real chat and the draft id clears.
			if (activeDsChatId === this._draftDsChatId) {
				this._activeDsChatStream = null;
				return;
			}
			this._activeDsChatStream = new DsChatPollingStream(activeDsChatId);
			this._activeDsChatStream.start();
		}
	}
}

new StreamSync();
